/**
 * Shimmer loading primitive (app-wide loading-state sweep, 2026-09-01).
 * Upgrades the static `<div className="h-8 w-28 bg-surface-elev rounded-md"
 * aria-hidden="true" />` placeholder convention (KpiValueCard,
 * MarginFlowCard) to an animated shimmer, and gives pages that currently
 * render nothing/bare "Loading…" text while a TanStack Query is in flight a
 * layout-shaped placeholder instead. The `.skeleton` visuals (base color,
 * shimmer sweep, reduced-motion override) live in index.css's utilities
 * layer next to `.rise`/`.breathe`.
 *
 * Never use these for an empty/"no data" state — only while a query is
 * actually loading. MarginFlowCard is the reference for keeping loading vs
 * module-off vs empty distinct.
 */
import { cn } from '../design/cn';

export interface SkeletonProps {
  /** Sizing/shape utilities (height, width, rounded-*) — same className
   *  contract as the static placeholder divs this replaces. */
  className?: string;
}

/** Base primitive: a shimmering block sized entirely by the caller. */
export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn('skeleton', className)} aria-hidden="true" />;
}

/** A single text-height line, for list rows and multi-line card bodies. */
export function SkeletonLine({ className }: SkeletonProps) {
  return <Skeleton className={cn('h-3.5 w-full rounded-xs', className)} />;
}

export interface SkeletonRowsProps {
  /** Number of placeholder `<tr>`s to render. */
  rows: number;
  /** One Tailwind width class per column, left to right — mirrors the real
   *  table's column widths so the skeleton doesn't jump on load. */
  colWidths: string[];
  /** Override the `<td>` padding/alignment classes to match the real table
   *  (defaults to this app's common `px-4 py-3 align-top`). */
  cellClassName?: string;
}

/** `rows` skeleton `<tr>`s for a `<tbody>`, one skeleton line per column
 *  width supplied. Each row is `aria-hidden` so screen readers skip the
 *  whole placeholder set rather than announcing empty cells one at a time. */
export function SkeletonRows({ rows, colWidths, cellClassName }: SkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i} aria-hidden="true">
          {colWidths.map((width, j) => (
            <td key={j} className={cellClassName ?? 'px-4 py-3 align-top'}>
              <SkeletonLine className={width} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
